import { composeClassNames } from "@faedev/editor-utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { CSSProperties, useRef } from "react";
import { Card, ListGroup } from "react-bootstrap";
import Property from "src/components/Property";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getLink from "src/functions/getLink";
import getSortedDiscounts from "src/functions/getSortedDiscounts";
import { sBenefitCard } from "src/styles/sBenefitCard.module.scss";
import { sBenefitCardBody } from "src/styles/sBenefitCardBody.module.scss";
import { sBenefitCardFooter } from "src/styles/sBenefitCardFooter.module.scss";
import { sListGroupDiscounts } from "src/styles/sListGroupDiscounts.module.scss";
import { ApiPartner } from "src/types/ApiPartner";

type Props = {
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  data: ApiPartner;
};

function BenefitCard({ style, className, data }: Props) {
  const { locale: lang } = useRouter();
  const targetRef = useRef();
  const bestDiscounts =
    data && data?.discounts.length > 0
      ? getSortedDiscounts(data?.discounts, true)
      : [];

  return data ? (
    <Card
      className={composeClassNames([sBenefitCard, className, "partner"])}
      style={style}
    >
      <div className={"card-img-wrapper"}>
        {data?.thumbnail || data?.logotype ? (
          <Card.Img
            src={
              constants.Paths.imagesUrl +
              (data?.thumbnail ? data?.thumbnail?.url : data?.logotype?.url)
            }
            ref={targetRef}
            alt={data.title}
          />
        ) : null}
        <Card.ImgOverlay className={`partner-img-overlay`}>
          <Link
            href={getLink(data.locale, `benefit`, data.slug, null)}
            passHref
            locale={false}
          >
            <Card.Link />
          </Link>
        </Card.ImgOverlay>
      </div>
      {bestDiscounts && bestDiscounts.length > 0 && (
        <div
          className={composeClassNames([
            sListGroupDiscounts,
            "list-group-discounts",
          ])}
        >
          <ListGroup horizontal={true}>
            {bestDiscounts.map((item, i) => (
              <ListGroup.Item
                key={`discount-${item.id}`}
                variant={item.group === "instructor" ? "secondary" : "primary"}
              >
                {`-${Number(item.value) * 100}%`}{" "}
                <Translate
                  tKey={`for ${item.group}${item.group !== "all" ? "s" : ""}`}
                />
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
      <Card.Body
        className={composeClassNames([sBenefitCardBody, "partner-body"])}
      >
        <Card.Title>
          <Link
            href={getLink(lang, `benefit`, data.slug, null)}
            passHref
            locale={lang}
          >
            <Card.Link>{data.title}</Card.Link>
          </Link>
        </Card.Title>
        {data.label && <Card.Text>{data.label}</Card.Text>}
      </Card.Body>
      <Card.Footer
        className={composeClassNames([sBenefitCardFooter, "partner-footer"])}
      >
        {data?.tags.length > 0 &&
          data.tags.map((item, i) => {
            return (
              i < constants.List.limitBenefitCardTags && (
                <Property
                  size="nm"
                  className={`partner-property`}
                  showIcon={false}
                  translation={true}
                  value={item.name}
                  key={`tag-${item.id}`}
                />
              )
            );
          })}
      </Card.Footer>
    </Card>
  ) : null;
}

export default BenefitCard;
